import { useState, useEffect } from 'react';
import { Cookie, X, Settings, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTermsAccepted } from '@/hooks/useTermsAccepted';

const COOKIE_CONSENT_KEY = 'tempvault_cookie_consent';

type ConsentStatus = 'pending' | 'accepted' | 'rejected';

export const useCookieConsent = () => {
  const [consent, setConsent] = useState<ConsentStatus>('pending');

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === 'accepted' || stored === 'rejected') {
      setConsent(stored);
    }
  }, []);

  return consent;
};

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const termsAccepted = useTermsAccepted();

  useEffect(() => {
    // Only show cookie banner after terms are accepted
    if (termsAccepted) {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!stored) {
        // Small delay to not overwhelm user after terms modal
        const timer = setTimeout(() => setVisible(true), 500);
        return () => clearTimeout(timer);
      }
    }
  }, [termsAccepted]);

  const handleAccept = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'accepted');
    setVisible(false);
    // Reload to initialize ads
    window.location.reload();
  };

  const handleReject = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, 'rejected');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 animate-in slide-in-from-bottom duration-300">
      <div className="max-w-4xl mx-auto glass-premium border border-primary/20 rounded-2xl p-6 shadow-glow">
        <div className="flex items-start gap-4">
          <div className="shrink-0">
            <div className="w-12 h-12 rounded-xl gradient-bg flex items-center justify-center">
              <Cookie className="w-6 h-6 text-primary-foreground" />
            </div>
          </div>
          
          <div className="flex-1 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-foreground">Cookie Preferences</h3>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                onClick={handleReject}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
            
            <p className="text-sm text-muted-foreground leading-relaxed">
              We use cookies to enhance your browsing experience and display personalized advertisements through Google AdSense. 
              By clicking "Accept All", you consent to our use of cookies as described in our Privacy Policy.
            </p>

            {showDetails && (
              <div className="space-y-3 pt-2 border-t border-border/50 animate-in fade-in duration-200">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Essential Cookies</p>
                    <p className="text-xs text-muted-foreground">Required for basic site functionality. Always enabled.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                  <div className="w-2 h-2 rounded-full bg-primary mt-1.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Advertising Cookies</p>
                    <p className="text-xs text-muted-foreground">Used by Google AdSense to show personalized ads based on your interests.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-secondary/50">
                  <div className="w-2 h-2 rounded-full bg-accent mt-1.5 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">Analytics Cookies</p>
                    <p className="text-xs text-muted-foreground">Help us understand how visitors interact with our website.</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Button
                onClick={handleAccept}
                className="gradient-bg text-primary-foreground shadow-glow hover:shadow-glow-intense transition-all"
              >
                <Check className="w-4 h-4 mr-2" />
                Accept All
              </Button>
              <Button
                variant="outline"
                onClick={handleReject}
                className="border-border/50 hover:bg-secondary/50"
              >
                Reject Non-Essential
              </Button>
              <Button
                variant="ghost"
                onClick={() => setShowDetails(!showDetails)}
                className="text-muted-foreground hover:text-foreground"
              >
                <Settings className="w-4 h-4 mr-2" />
                {showDetails ? 'Hide Details' : 'Cookie Details'}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
